import type {
  InjectionProtocol,
  ProtocolBrand,
  ProtocolWarning,
} from "../types";

/**
 * Follitropin delta — the pen dosed in micrograms.
 *
 * Every other gonadotropin here is dialled in international units. This one is
 * not, and its label says the microgram dose "cannot be applied to other
 * gonadotropins": the number on her prescription is meaningless on any other
 * pen, and an IU number is meaningless on this one. The dose guard turns a
 * record in IU into a question rather than a sentence, and step 1 says
 * "micrograms" out loud.
 *
 * ### What the pen does that the others do not
 *
 * - **It is primed once**, on first use, by dialling to a droplet symbol
 *   rather than to a number, and the label insists on it even when no bubble
 *   is visible. Skip it and the first dose of the cycle is silently short.
 * - **The dial stops at 20 and a daily dose can be 24.** The label's answer is
 *   the split dose: inject what the pen can dial, prime a new pen, inject the
 *   rest. That is a caution on the dialling step, in the label's words, and it
 *   is the only place in the catalogue a label spells out dose arithmetic.
 * - **The dose is fixed for the whole cycle.** No mid-course titration, which
 *   is the opposite of Gonal-F; a patient used to one may expect the other.
 * - **The first injection is given under supervision.** Said on the first
 *   card, as the label says it, so a patient on day one is not alone with a
 *   walk that assumes she is trained.
 *
 * ### Market
 *
 * EMA-authorised, not an FDA product: one leaflet, read in English and French
 * (`docs/features/injection-coach/medications/rekovelle.md`). The content was
 * rewritten against it first — it carried a needle gauge the label never
 * states, a ten-second hold where the label counts to five, and a two-hour
 * missed-dose rule that no gonadotropin label has.
 *
 * Restated from `content/data/medications/gonadotropins.json` (the `Rekovelle`
 * entry). A protocol may omit from the approved content but never add to it.
 */

const REKOVELLE_BRANDS = [
  {
    name: "Rekovelle",
    markets: ["EU", "GB", "CH", "CA", "AU", "JP", "KR", "SG"],
  },
] as const satisfies readonly ProtocolBrand[];

/** The leaflet's own words, under "How are injections given". */
const FIRST_INJECTION: ProtocolWarning = {
  id: "rekovelle.first-injection-under-supervision",
  severity: "caution",
  escalation: "none",
  copy: {
    en: "First injection - The first injection of this medicine should be given under the supervision of a doctor or nurse; give yourself the following ones at home only after you have been trained.",
    fr: "Première injection - La première injection de ce médicament doit être faite sous la surveillance d'un médecin ou d'une infirmière ; faites les suivantes chez vous seulement après avoir été formée.",
  },
  sourceSegment:
    "⚠ First injection - The first injection of this medicine should be given under the supervision of a doctor or nurse; give yourself the following ones at home only after you have been trained.",
};

/** No hour, as for every gonadotropin: do not double, contact the clinic. */
const MISSED_DOSE: ProtocolWarning = {
  id: "rekovelle.missed-dose",
  severity: "caution",
  escalation: "none",
  copy: {
    en: "Missed dose - Do not take a double dose to make up for a forgotten dose. Contact your clinic as soon as you notice that you forgot a dose.",
    fr: "Dose oubliée - Ne prenez pas de dose double pour remplacer une dose oubliée. Contactez votre centre dès que vous vous rendez compte que vous avez oublié une dose.",
  },
  sourceSegment:
    "⚠ Missed dose - Do not take a double dose to make up for a forgotten dose. Contact your clinic as soon as you notice that you forgot a dose.",
};

/**
 * The split dose, where it bites: at the dial.
 *
 * The pen will not let her dial more than it holds, so the first sign is a
 * knob that stops short. The label's answer is to inject what is there and
 * finish with a new pen — the one place a label asks a patient to subtract —
 * and it sends her to the clinic with questions about the arithmetic.
 */
const SPLIT_DOSE: ProtocolWarning = {
  id: "rekovelle.not-enough-medicine-for-the-full-dose",
  severity: "caution",
  escalation: "none",
  copy: {
    en: "Not enough medicine for the full dose - If the dose knob will not turn to your prescribed dose, the pen does not have enough medicine left. Inject what is left and complete your dose with a new pen, or use a new pen for the full dose. Call your clinic if you have questions about the split dose.",
    fr: "Pas assez de médicament pour la dose complète - Si le sélecteur ne tourne pas jusqu'à votre dose prescrite, il ne reste pas assez de médicament dans le stylo. Injectez ce qu'il reste et complétez votre dose avec un nouveau stylo, ou utilisez un nouveau stylo pour la dose complète. Appelez votre centre si vous avez une question sur la dose fractionnée.",
  },
  sourceSegment:
    "⚠ Not enough medicine for the full dose - If the dose knob will not turn to your prescribed dose, the pen does not have enough medicine left. Inject what is left and complete your dose with a new pen, or use a new pen for the full dose. Call your clinic if you have questions about the split dose.",
};

/** From the IFU's warnings: never force the button. */
const BUTTON_HARD_TO_PUSH: ProtocolWarning = {
  id: "rekovelle.button-hard-to-push",
  severity: "caution",
  escalation: "none",
  copy: {
    en: "Button hard to push - If the injection button is not easy to push in, do not use force: change the needle, and if it is still hard, use a new pen.",
    fr: "Bouton dur à enfoncer - Si le bouton d'injection s'enfonce difficilement, ne forcez pas : changez d'aiguille, et si c'est toujours dur, prenez un nouveau stylo.",
  },
  sourceSegment:
    "⚠ Button hard to push - If the injection button is not easy to push in, do not use force: change the needle, and if it is still hard, use a new pen.",
};

/**
 * OHSS, by its symptoms, to the clinic — the leaflet's list minus difficulty
 * breathing, which goes to emergency below as for every medication here.
 */
const OHSS: ProtocolWarning = {
  id: "rekovelle.ovarian-hyperstimulation",
  severity: "stop",
  escalation: "clinic-now",
  copy: {
    en: "Ovarian hyperstimulation - Contact your clinic immediately for pain, discomfort or swelling of the abdomen, nausea, vomiting, diarrhoea or weight gain.",
    fr: "Hyperstimulation ovarienne - Contactez votre centre immédiatement en cas de douleur, gêne ou gonflement du ventre, nausées, vomissements, diarrhée ou prise de poids.",
  },
  sourceSegment:
    "⚠ Ovarian hyperstimulation - Contact your clinic immediately for pain, discomfort or swelling of the abdomen, nausea, vomiting, diarrhoea or weight gain.",
};

/**
 * A needle that broke under the skin. The IFU's words are "get medical help
 * right away"; it is a stop at the needle and a call to make now, not a walk
 * to continue.
 */
const BROKEN_NEEDLE: ProtocolWarning = {
  id: "rekovelle.broken-needle",
  severity: "stop",
  escalation: "clinic-now",
  copy: {
    en: "Broken needle - If a broken needle stays stuck under your skin, get medical help right away.",
    fr: "Aiguille cassée - Si une aiguille cassée reste sous la peau, consultez un médecin tout de suite.",
  },
  sourceSegment:
    "⚠ Broken needle - If a broken needle stays stuck under your skin, get medical help right away.",
};

const BREATHING_OR_ALLERGIC: ProtocolWarning = {
  id: "rekovelle.breathing-or-allergic-reaction",
  severity: "stop",
  escalation: "emergency",
  copy: {
    en: "Seek urgent help - Difficulty breathing or signs of an allergic reaction need emergency services straight away.",
    fr: "Aide urgente - Des difficultés respiratoires ou des signes de réaction allergique nécessitent d'appeler les secours tout de suite.",
  },
  sourceSegment:
    "⚠ Seek urgent help - Difficulty breathing or signs of an allergic reaction need emergency services straight away.",
};

export const REKOVELLE_PROTOCOL: InjectionProtocol = {
  medicationSlug: "rekovelle",
  brands: REKOVELLE_BRANDS,
  aliases: ["Follitropin delta", "Follitropine delta"],
  indication: "stimulation",
  // Micrograms, and no fixed list: the dose is computed per patient from AMH
  // and weight and rounded to the pen's 0.33 steps. Any number in the right
  // unit may be hers; a number in IU never is.
  dose: { unit: "mcg" },
  form: "pen",
  prescriptionForms: ["pre_filled_pen"],
  route: "subcutaneous",
  version: "2026-10-05.1",
  derivedFrom: "content/data/medications/gonadotropins.json#follitropin-delta",
  steps: [
    {
      id: "rekovelle.pen.prepare",
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Prepare - Wash your hands. Check that the pen is not damaged and that the medicine in the cartridge is clear and does not contain particles. Make sure you have the correct pen with the correct strength, and check the expiry date on the pen label. Your dose is in micrograms and is fixed for the whole treatment period.",
        fr: "Préparer - Lavez-vous les mains. Vérifiez que le stylo n'est pas endommagé et que la solution dans la cartouche est limpide et ne contient pas de particules. Assurez-vous d'avoir le stylo correct avec le bon dosage, et vérifiez la date de péremption sur l'étiquette du stylo. Votre dose est indiquée en microgrammes et reste fixe pour toute la période de traitement.",
      },
      warnings: [FIRST_INJECTION, MISSED_DOSE],
    },
    {
      // The outer cap is kept on purpose: it is what takes the needle off
      // afterwards, and a patient who threw it away has a bare needle to
      // unscrew.
      id: "rekovelle.pen.attach-the-needle",
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Attach a new needle - Pull off the pen cap. Pull off the protective foil from a new needle and click it on; you will hear or feel a click when it is safely on. Pull off the outer needle cap and keep it, you will need it to remove the needle after the injection. Pull off the inner needle cap and throw it away. Only use the needles supplied with the pen, and a new one for each injection.",
        fr: "Fixer une aiguille neuve - Retirez le capuchon du stylo. Retirez la languette de protection d'une aiguille neuve et enclenchez-la ; vous entendrez ou sentirez un clic lorsqu'elle sera bien fixée. Retirez le capuchon extérieur de l'aiguille et gardez-le, vous en aurez besoin pour retirer l'aiguille après l'injection. Retirez le capuchon intérieur de l'aiguille et jetez-le. N'utilisez que les aiguilles fournies avec le stylo, et une aiguille neuve à chaque injection.",
      },
    },
    {
      // `verify` and confirmed: a look she has to take, on day one of the
      // cycle, and the label wants it done even when no bubble shows.
      id: "rekovelle.pen.check-for-a-droplet",
      kind: "verify",
      requiresConfirmation: true,
      copy: {
        en: "Prime a new pen - On a pen you are using for the first time only: turn the dose knob until the droplet symbol lines up with the dose indicator. Hold the pen with the needle pointing upwards and tap the cartridge holder so any air bubbles rise to the top. With the needle still pointing up, press the injection button all the way in until 0 lines up with the dose indicator, and check that a droplet appears at the tip of the needle. If no droplet appears, repeat; after five tries, change the needle and prime again; if there is still none, use a new pen. On a pen you have already used, skip this step.",
        fr: "Amorcer un stylo neuf - Seulement la première fois que vous utilisez un stylo : tournez le sélecteur de dose jusqu'à ce que le symbole de gouttelette s'aligne avec l'indicateur de dose. Tenez le stylo aiguille vers le haut et tapotez le porte-cartouche pour faire monter les bulles d'air. L'aiguille toujours vers le haut, appuyez sur le bouton d'injection à fond jusqu'à voir le 0 aligné avec l'indicateur de dose, et vérifiez qu'une gouttelette apparaît à la pointe de l'aiguille. Si aucune gouttelette n'apparaît, recommencez ; après cinq essais, changez d'aiguille et amorcez à nouveau ; si toujours rien, prenez un nouveau stylo. Sur un stylo déjà utilisé, passez cette étape.",
      },
    },
    {
      id: "rekovelle.pen.ready-the-pen",
      kind: "verify",
      requiresConfirmation: true,
      copy: {
        en: "Set your dose - Turn the dose knob until your prescribed dose{dose} lines up with the dose indicator in the dose display window; each click is one increment. You can correct the dose up or down without losing medicine. Do not press the injection button while you dial.",
        fr: "Régler la dose - Tournez le sélecteur de dose jusqu'à ce que votre dose prescrite{dose} s'aligne avec l'indicateur de dose dans la fenêtre d'affichage ; chaque clic est un intervalle. Vous pouvez corriger la dose dans un sens ou dans l'autre sans perte de médicament. N'appuyez pas sur le bouton d'injection pendant que vous réglez la dose.",
      },
      warnings: [SPLIT_DOSE],
    },
    {
      id: "rekovelle.pen.clean-site",
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Clean the site - Wipe the skin of your abdomen with an alcohol swab and do not touch the area again before you inject. Use a new injection site each time, and do not inject where the skin is sore, bruised, red, hard, scarred or has stretch marks.",
        fr: "Désinfecter le site - Nettoyez la peau du ventre avec une compresse imbibée d'alcool et ne touchez plus cette zone avant l'injection. Changez de site à chaque injection, et n'injectez pas dans une zone douloureuse, rouge, dure, marquée, qui présente un bleu ou des vergetures.",
      },
    },
    {
      // No angle in this label — "straight into your skin as shown by your
      // healthcare provider" — so none is said. The five is the label's.
      id: "rekovelle.pen.inject",
      kind: "irreversible",
      requiresConfirmation: true,
      copy: {
        en: "Inject - Hold the pen so the dose display window stays visible. Pinch your skin and insert the needle straight into your skin. Place your thumb on the injection button, press it all the way in and hold. When 0 lines up with the dose indicator, slowly count to 5 while still holding the button, then release it and pull the needle straight out. Do not tilt the pen.",
        fr: "Injecter - Tenez le stylo de façon à voir la fenêtre d'affichage de dose. Pincez la peau et insérez l'aiguille droit dans la peau. Placez le pouce sur le bouton d'injection, appuyez à fond et maintenez. Quand le 0 est aligné avec l'indicateur de dose, comptez lentement jusqu'à 5 en maintenant le bouton, puis relâchez-le et retirez l'aiguille droit. N'inclinez pas le stylo.",
      },
      warnings: [BUTTON_HARD_TO_PUSH, BROKEN_NEEDLE, BREATHING_OR_ALLERGIC],
    },
    {
      id: "rekovelle.pen.aftercare",
      kind: "aftercare",
      requiresConfirmation: false,
      copy: {
        en: "After injection - If blood appears, press a gauze pad lightly on the site. Carefully put the outer needle cap back on with a firm push, unscrew the needle and dispose of it in a sharps container right away; never store the pen with a needle attached. Put the pen cap back on. Keep the pen at or below 25 degrees Celsius for up to 28 days after the first use, then discard it.",
        fr: "Après l'injection - Si du sang apparaît, appuyez légèrement avec une compresse. Replacez soigneusement le capuchon extérieur de l'aiguille d'une poussée ferme, dévissez l'aiguille et jetez-la tout de suite dans un collecteur ; ne rangez jamais le stylo avec une aiguille fixée. Remettez le capuchon du stylo. Conservez le stylo à 25 degrés Celsius maximum pendant 28 jours au plus après la première utilisation, puis jetez-le.",
      },
      warnings: [OHSS],
    },
  ],
};
